import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { FronteggAuthService } from '@frontegg/angular';
import { TranslatePipe } from '@ngx-translate/core';
import { MessageModule } from 'primeng/message';
import { ProgressSpinner } from 'primeng/progressspinner';
import { filter, take } from 'rxjs';
import { environment } from '../../../environments/environment';

export type PrinterAuthorizeStatus = 'working' | 'redirecting' | 'error';

/** Everything needed to build the Frontegg authorize request for the virtual printer. */
export interface PrinterAuthorizeRequest {
  codeChallenge: string;
  codeChallengeMethod: string;
  state: string;
  tenantId: string;
  organization: string;
}

// RFC 7636: an S256 challenge is 43 base64url characters.
const CODE_CHALLENGE_PATTERN = /^[A-Za-z0-9_-]{43}$/;
const MIN_STATE_LENGTH = 8;
const MAX_STATE_LENGTH = 256;

/**
 * Authorizes the Softdocs virtual printer. The printer opens this page in the browser with its PKCE
 * challenge and state. By the time this page renders, the parent Web Capture page has already signed
 * the user in (including Content URL setup) and checked the `virtual-printer-access` flag before the
 * login started. This page sends the browser through Frontegg's authorize step, which returns the
 * authorization code to the printer's loopback redirect.
 */
@Component({
  selector: 'app-printer-authorize',
  templateUrl: './printer-authorize.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MessageModule, ProgressSpinner, TranslatePipe]
})
export class PrinterAuthorizeComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly fronteggAuthService = inject(FronteggAuthService);
  private readonly destroyRef = inject(DestroyRef);

  readonly status = signal<PrinterAuthorizeStatus>('working');
  readonly errorKey = signal<string>('printerAuthFailed');

  /**
   * Reads the printer's PKCE parameters from the URL, and starts the Frontegg authorization.
   */
  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const codeChallenge = params.get('code_challenge') ?? '';
    const codeChallengeMethod = params.get('code_challenge_method') ?? '';
    const state = params.get('state') ?? '';

    if (!this.isValidPrinterRequest(codeChallenge, codeChallengeMethod, state)) {
      this.fail('printerAuthInvalidRequest');
      return;
    }

    if (!environment.printer.appId) {
      console.error('[PrinterAuthorize] The printer application is not configured.');
      this.fail('printerAuthFailed');
      return;
    }

    // The sign-in can finish a moment after this page renders, so wait for Frontegg to report a signed-in user.
    this.fronteggAuthService.authState$
      .pipe(
        filter(authState => authState.isAuthenticated && !!authState.user?.accessToken),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: authState => {
          const tenantId = localStorage.getItem('tenantId') || authState.user?.tenantId;
          const organization = localStorage.getItem('vanityName');
          if (!tenantId || !organization) {
            console.error(
              '[PrinterAuthorize] The tenant or organization (vanity name) is unknown.'
            );
            this.fail('printerAuthFailed');
            return;
          }
          this.authorize({ codeChallenge, codeChallengeMethod, state, tenantId, organization });
        },
        error: error => {
          console.error('[PrinterAuthorize] The sign-in state could not be read:', error);
          this.fail('printerAuthFailed');
        }
      });
  }

  /**
   * @param codeChallenge - The PKCE challenge from the printer
   * @param codeChallengeMethod - The PKCE method from the printer
   * @param state - The state from the printer
   * @returns true when the printer's parameters are well formed
   */
  isValidPrinterRequest(
    codeChallenge: string,
    codeChallengeMethod: string,
    state: string
  ): boolean {
    return (
      codeChallengeMethod === 'S256' &&
      CODE_CHALLENGE_PATTERN.test(codeChallenge) &&
      state.length >= MIN_STATE_LENGTH &&
      state.length <= MAX_STATE_LENGTH
    );
  }

  /**
   * Builds the Frontegg authorize URL that returns an authorization code to the printer.
   * The redirect target is fixed in the environment and never taken from the request. No `prompt` is
   * sent, so an existing Frontegg session is reused without asking the user to sign in again.
   * @param request - The printer's challenge and state, and the user's tenant
   * @returns The URL to send the browser to
   */
  buildAuthorizeUrl(request: PrinterAuthorizeRequest): string {
    const { baseUrl, clientId } = environment.frontegg.moduleConfig.contextOptions;

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      redirect_uri: environment.printer.redirectUri,
      scope: 'openid email profile',
      code_challenge: request.codeChallenge,
      code_challenge_method: request.codeChallengeMethod,
      state: request.state,
      appId: environment.printer.appId,
      tenantId: request.tenantId,
      organization: request.organization
    });

    return `${baseUrl}/frontegg/oauth/authorize?${params.toString()}`;
  }

  /**
   * @param url - A URL to check
   * @returns true when the URL is https on the Frontegg login host for this environment
   */
  isFronteggUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      const expected = new URL(environment.frontegg.moduleConfig.contextOptions.baseUrl);
      return parsed.protocol === 'https:' && parsed.origin === expected.origin;
    } catch {
      return false;
    }
  }

  /**
   * Sends the browser to the given URL. Separate from the authorize step so tests can replace it.
   * @param url - The URL to navigate to
   */
  navigateTo(url: string): void {
    window.location.assign(url);
  }

  /**
   * Builds the Frontegg authorize URL and sends the browser there.
   * @param request - The printer's challenge and state and the user's tenant
   */
  private authorize(request: PrinterAuthorizeRequest): void {
    const url = this.buildAuthorizeUrl(request);
    if (!this.isFronteggUrl(url)) {
      console.error('[PrinterAuthorize] Refusing to redirect away from the Frontegg login host.');
      this.fail('printerAuthFailed');
      return;
    }

    this.status.set('redirecting');
    this.navigateTo(url);
  }

  /**
   * @param key - Translation key of the message to show
   */
  private fail(key: string): void {
    this.errorKey.set(key);
    this.status.set('error');
  }
}
